import { useEffect, useRef, useState } from 'react';
import { useParams } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { admin } from '../../api/client.js';
import { useSession } from '../../context/SessionContext.jsx';
import { framedUrl, replyToFrameMessage, themeMessage } from '../../lib/embedTheme.js';

/**
 * An admin-menu application: a tool built by another workstream, running on
 * its own App Service, framed here and told who is looking at it.
 *
 * Deliberately the same mechanics as WebsiteView - framedUrl, the theme
 * handshake, the stalled-frame overlay - because they are the same problem and
 * the partner-facing contract in docs/11-EMBEDDED-APP-STYLE-GUIDE.md is
 * written once. Not literally the same component, because the two differ in
 * every respect that matters at the edges: the target comes from configuration
 * rather than a profile item, the token carries `adm: true`, and there is no
 * "no token" branch. An admin app is always given a token; that is the point
 * of it being on this menu.
 *
 * What is NOT here, and should stay absent: any decision about what the app
 * may show. The portal asserts who the viewer is and that they administer this
 * customer. Everything after that is the app's, verified from the signature -
 * see §7 of the style guide.
 */
export default function AdminApp() {
  const { key } = useParams();
  const { theme } = useSession();
  const [frame, setFrame] = useState(null);
  const [error, setError] = useState(null);
  const iframeRef = useRef(null);

  /* Read during render so an effect sees the live value without subscribing.
     The dependency array below is why - see WebsiteView for the full account:
     adding `theme` there reloads the frame on every toggle and burns a
     five-minute credential per click. */
  const themeRef = useRef(theme);
  themeRef.current = theme;

  const { data: apps = [] } = useQuery({
    queryKey: ['admin', 'apps'], queryFn: admin.listApps, retry: false
  });
  const app = apps.find(a => a.key === key);

  useEffect(() => {
    let cancelled = false;
    setFrame(null);
    setError(null);
    if (!key) return;

    (async () => {
      try {
        const { url, token } = await admin.getAppToken(key);
        if (cancelled) return;
        setFrame(framedUrl({ targetUrl: url, token, theme: themeRef.current }));
      } catch (err) {
        if (!cancelled) setError(err);
      }
    })();

    return () => { cancelled = true; };
  }, [key]);

  const [frameLoaded, setFrameLoaded] = useState(false);
  const [slow, setSlow] = useState(false);

  useEffect(() => {
    setFrameLoaded(false);
    setSlow(false);
    if (!frame) return;
    const t = setTimeout(() => setSlow(true), 12_000);
    return () => clearTimeout(t);
  }, [frame]);

  // Skipped on the first run: the URL already carries the theme, and posting
  // before the app has its listener achieves nothing.
  const posted = useRef(false);
  useEffect(() => {
    if (!frame) { posted.current = false; return; }
    if (!posted.current) { posted.current = true; return; }
    iframeRef.current?.contentWindow?.postMessage(themeMessage(theme), frame.origin);
  }, [theme, frame]);

  // A module script is deferred by specification, so the app's listener may not
  // exist when the URL is minted. `vizora:ready` closes that gap.
  useEffect(() => {
    if (!frame) return;
    function onMessage(event) {
      const reply = replyToFrameMessage(event, {
        frameOrigin: frame.origin,
        frameWindow: iframeRef.current?.contentWindow,
        theme: themeRef.current
      });
      if (reply) event.source?.postMessage(themeMessage(reply), frame.origin);
    }
    window.addEventListener('message', onMessage);
    return () => window.removeEventListener('message', onMessage);
  }, [frame]);

  const label = app?.label ?? 'This application';

  if (error) {
    return (
      <div className="admin-page">
        <div className="alert alert-danger">
          Could not open {label}: {error.body?.error ?? error.message}
        </div>
      </div>
    );
  }

  return (
    <div className="embed-wrapper">
      {!frame && <div className="embed-overlay"><div className="spinner-border text-primary" /></div>}

      {/* Only when the frame genuinely never loaded. An app that loaded and
          then showed its own error is not something the portal can see, and
          claiming otherwise would be worse than saying nothing. */}
      {frame && slow && !frameLoaded && (
        <div className="embed-overlay">
          <div className="embed-stalled">
            <i className="bi bi-exclamation-triangle" aria-hidden="true" />
            <p className="fw-semibold mb-1">{label} is not responding</p>
            <p className="mb-2">
              The portal asked <code>{frame.origin}</code> for this page and it has
              not loaded. That usually means the application is down, or its
              address in <code>ADMIN_APPS</code> is wrong.
            </p>
            {/* The same escape hatch WebsiteView offers, and for the same
                reason: opening the origin directly separates "the application
                is down" from "the portal cannot frame it", which are the two
                causes this overlay cannot tell apart. Without it the only way
                to find out is to retype the origin by hand from the sentence
                above. `frame.src` rather than the bare origin, so the token
                travels and the app can actually render. */}
            <a className="btn btn-sm btn-primary" href={frame.src}
               target="_blank" rel="noopener noreferrer">Try it in a new tab</a>
            <p className="embed-stalled-note mb-0">
              If it works in a new tab but not here, the application is
              refusing to be framed — check its <code>frame-ancestors</code>
              and that it sends no <code>X-Frame-Options</code>. Either way
              this is a Vizora-configured application, so it is a Vizora
              problem rather than something to change on this customer.
            </p>
          </div>
        </div>
      )}

      {frame && (
        <iframe
          ref={iframeRef}
          title={label}
          src={frame.src}
          className="embed-container"
          sandbox="allow-scripts allow-forms allow-same-origin allow-downloads"
          referrerPolicy="strict-origin"
          allowFullScreen
          onLoad={() => setFrameLoaded(true)}
        />
      )}
    </div>
  );
}
